import type { i18n } from "i18next";
import type { AccessHandle } from "../layoutHandles.ts";
import type { LinkIcon } from "../link.ts";
import { ADMIN_MENU_ID, TOP_MENU_ID, USER_MENU_ID } from "../menuIds.ts";

export interface CommandMenuLink {
    id: string;
    to: string;
    groupId: string;
    icon?: LinkIcon;
    priority: number;
    menuId?: string;
    access?: AccessHandle;
    i18next: {
        instance: i18n;
        namespace: string;
        labelKey: string;
        descriptionKey: string;
        parentLabelKey: string;
        groupLabelKey: string;
    };
}

const TierBase: Record<string, number> = { [TOP_MENU_ID]: 30_000, [ADMIN_MENU_ID]: 20_000, [USER_MENU_ID]: 10_000 };

export class CommandMenuLinksRegistry {
    readonly #links = new Map<string, CommandMenuLink>();
    #snapshot: readonly CommandMenuLink[] = [];
    readonly #deferredIds = new Set<string>();
    #staleIds: Set<string> | undefined;

    registerLink(link: CommandMenuLink) {
        const priority = link.priority + (link.menuId ? (TierBase[link.menuId] ?? 0) : 0);
        this.#links.set(link.id, { ...link, priority });

        if (this.#staleIds) {
            this.#deferredIds.add(link.id);
            this.#staleIds.delete(link.id);
        }

        this.#snapshot = Array.from(this.#links.values());
    }

    beginDeferredScope() {
        const staleIds = new Set(this.#deferredIds);
        this.#staleIds = staleIds;

        return () => {
            for (const id of staleIds) {
                this.#links.delete(id);
                this.#deferredIds.delete(id);
            }

            this.#staleIds = undefined;
            this.#snapshot = Array.from(this.#links.values());
        };
    }

    getSnapshot(): readonly CommandMenuLink[] {
        return this.#snapshot;
    }
}
